import React, { useState, useRef, useEffect } from 'react';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

/**
 * MentionTextarea
 * Detects `@` typing, shows a dropdown of project members, and inserts
 * a token `@[Name](user_xxx)` on selection. The token is what's stored
 * in the comment content. Backend parses this pattern to extract mentions.
 *
 * Props:
 *  - value, onChange: standard controlled textarea
 *  - members: [{ user_id, name, email, avatar }]
 *  - placeholder, rows, className, onPaste, autoFocus, dataTestId
 */
export default function MentionTextarea({
  value,
  onChange,
  members = [],
  placeholder,
  rows = 3,
  className = '',
  onPaste,
  autoFocus = false,
  dataTestId,
  onKeyDownExtra,
}) {
  const textareaRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState('');
  const [anchor, setAnchor] = useState(0); // index of @ in value
  const [activeIdx, setActiveIdx] = useState(0);

  const filtered = (members || [])
    .filter(m => m.user_id && m.name)
    .filter(m => !query || m.name.toLowerCase().includes(query.toLowerCase()) || (m.email || '').toLowerCase().includes(query.toLowerCase()))
    .slice(0, 6);

  useEffect(() => { setActiveIdx(0); }, [query, showDropdown]);

  const updateMentionState = (newVal, caretPos) => {
    // Look backward from caret to find an '@' that starts a mention
    const upToCaret = newVal.slice(0, caretPos);
    const atIdx = upToCaret.lastIndexOf('@');
    if (atIdx === -1) { setShowDropdown(false); return; }
    // Ensure '@' is at start or preceded by whitespace / newline
    const charBefore = atIdx === 0 ? ' ' : upToCaret[atIdx - 1];
    if (!/\s/.test(charBefore) && atIdx !== 0) { setShowDropdown(false); return; }
    const q = upToCaret.slice(atIdx + 1);
    // If query contains whitespace or bracket, close
    if (/[\s\n\]\)]/.test(q)) { setShowDropdown(false); return; }
    setAnchor(atIdx);
    setQuery(q);
    setShowDropdown(true);
  };

  const handleChange = (e) => {
    const newVal = e.target.value;
    onChange(newVal);
    setTimeout(() => {
      const ta = textareaRef.current;
      if (ta) updateMentionState(newVal, ta.selectionStart);
    }, 0);
  };

  const selectMention = (user) => {
    const ta = textareaRef.current;
    const caretPos = ta ? ta.selectionStart : value.length;
    const before = value.slice(0, anchor);
    const after = value.slice(caretPos);
    const token = `@[${user.name}](${user.user_id})`;
    const inserted = `${before}${token} ${after}`;
    onChange(inserted);
    setShowDropdown(false);
    setTimeout(() => {
      if (ta) {
        const newPos = (before + token + ' ').length;
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (showDropdown && filtered.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % filtered.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(filtered[activeIdx]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setShowDropdown(false); return; }
    }
    if (onKeyDownExtra) onKeyDownExtra(e);
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={onPaste}
        placeholder={placeholder}
        className={className}
        rows={rows}
        autoFocus={autoFocus}
        data-testid={dataTestId}
        onBlur={() => setTimeout(() => setShowDropdown(false), 120)}
      />
      {showDropdown && filtered.length > 0 && (
        <div
          className="absolute z-50 left-2 bottom-full mb-1 bg-white border border-zinc-200 rounded-lg shadow-lg w-64 max-h-56 overflow-y-auto"
          data-testid="mention-dropdown"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-zinc-400 border-b border-zinc-100 font-semibold">Mention a teammate</div>
          {filtered.map((u, idx) => (
            <button
              key={u.user_id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); selectMention(u); }}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${idx === activeIdx ? 'bg-blue-50' : 'hover:bg-zinc-50'}`}
              data-testid={`mention-option-${u.user_id}`}
            >
              <Avatar className="h-6 w-6 flex-shrink-0">
                <AvatarImage src={u.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-blue-400 to-indigo-500 text-white text-[9px] font-bold">
                  {u.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-zinc-800 truncate">{u.name}</div>
                {u.email && <div className="text-[10px] text-zinc-500 truncate">{u.email}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Renders comment content, converting @[Name](user_id) tokens
 * into highlighted mention pills and ![alt](url) markdown images.
 */
export function MentionContent({ content = '', currentUserId }) {
  if (!content) return <span className="text-zinc-400 italic">(empty)</span>;
  // Split by image markdown first, then mentions inside each text chunk
  const imgRe = /!\[.*?\]\(.*?\)/g;
  const mentionRe = /@\[([^\]]+)\]\((user_[a-zA-Z0-9]+)\)/g;
  const nodes = [];
  let key = 0;
  let lastIdx = 0;
  let m;
  while ((m = imgRe.exec(content)) !== null) {
    if (m.index > lastIdx) pushText(nodes, content.slice(lastIdx, m.index), currentUserId, () => key++, mentionRe);
    const imgMatch = m[0].match(/!\[(.*?)\]\((.*?)\)/);
    if (imgMatch) nodes.push(<img key={key++} src={imgMatch[2]} alt={imgMatch[1]} className="max-w-full rounded-md border border-zinc-200 my-2" />);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < content.length) pushText(nodes, content.slice(lastIdx), currentUserId, () => key++, mentionRe);
  return <span className="whitespace-pre-wrap">{nodes}</span>;
}

function pushText(nodes, text, currentUserId, nextKey, mentionRe) {
  mentionRe.lastIndex = 0;
  let last = 0;
  let m;
  while ((m = mentionRe.exec(text)) !== null) {
    if (m.index > last) nodes.push(<span key={nextKey()}>{text.slice(last, m.index)}</span>);
    const isSelf = m[2] === currentUserId;
    nodes.push(
      <span
        key={nextKey()}
        className={`inline-block px-1.5 py-0.5 rounded font-semibold text-[12px] ${isSelf ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-700'}`}
        data-testid={`mention-${m[2]}`}
      >@{m[1]}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(<span key={nextKey()}>{text.slice(last)}</span>);
}
